import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, sendOTP, resetPassword } from '../services/api';
import './Login.css';

const Login = ({ setUser }) => {
    const [buyerId, setBuyerId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Forgot Password States
    const [showForgot, setShowForgot] = useState(false);
    const [forgotStep, setForgotStep] = useState(1); // 1: Identifier, 2: OTP & New Pass
    const [identifier, setIdentifier] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!buyerId.trim() || !password.trim()) {
            setError('Please enter both Buyer ID and Password');
            return;
        }

        setLoading(true);

        try {
            const response = await loginUser(buyerId.trim(), password);

            if (response.success) {
                // Store user data
                const userData = {
                    buyerId: response.data.buyerId,
                    fullName: response.data.fullName,
                };

                setUser(userData);
                localStorage.setItem('cwUser', JSON.stringify(userData));

                // Navigate to dashboard
                navigate('/dashboard');
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!identifier.trim()) {
            setError('Please enter your Buyer ID or Email');
            return;
        }

        setLoading(true);
        try {
            const response = await sendOTP(identifier.trim());
            if (response.success) {
                setSuccess(response.message);
                setForgotStep(2);
            } else {
                setError(response.message || 'Failed to send OTP');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!otp.trim() || !newPassword.trim()) {
            setError('Please enter both OTP and New Password');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        try {
            const response = await resetPassword(identifier.trim(), otp.trim(), newPassword);
            if (response.success) {
                setSuccess(response.message);
                // Switch back to login after a delay
                setTimeout(() => {
                    setShowForgot(false);
                    setForgotStep(1);
                    setSuccess('');
                    setBuyerId(identifier); // Pre-fill buyerId if it was used as identifier
                }, 3000);
            } else {
                setError(response.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (showForgot) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h1>Reset Password</h1>
                        <p>{forgotStep === 1 ? 'Enter your details to receive an OTP' : 'Enter the OTP sent to your email'}</p>
                    </div>

                    {forgotStep === 1 ? (
                        <form onSubmit={handleSendOTP} className="login-form">
                            <div className="form-group">
                                <label htmlFor="identifier">Buyer ID or Email Address</label>
                                <input
                                    type="text"
                                    id="identifier"
                                    placeholder="Enter Buyer ID or Email"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                            {error && <div className="error-message">{error}</div>}
                            {success && <div className="success-message">{success}</div>}
                            <button type="submit" className="btn-primary" disabled={loading}>
                                {loading ? 'Sending...' : 'Send OTP'}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword} className="login-form">
                            <div className="form-group">
                                <label htmlFor="otp">Enter 6-Digit OTP</label>
                                <input
                                    type="text"
                                    id="otp"
                                    placeholder="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    disabled={loading}
                                    maxLength="6"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    placeholder="Enter your new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={loading}
                                />
                            </div>
                            {error && <div className="error-message">{error}</div>}
                            {success && <div className="success-message">{success}</div>}
                            <button type="submit" className="btn-primary" disabled={loading}>
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    )}

                    <div className="login-footer">
                        <button 
                            type="button" 
                            className="back-to-login-btn" 
                            onClick={() => { setShowForgot(false); setForgotStep(1); setSuccess(''); setError(''); }}
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Collaboration World</h1>
                    <p>Client Portal Login</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label htmlFor="buyerId">Buyer ID</label>
                        <input
                            type="text"
                            id="buyerId"
                            placeholder="Enter your Buyer ID (e.g., CW00001)"
                            value={buyerId}
                            onChange={(e) => setBuyerId(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                        />
                        <button 
                            type="button" 
                            className="forgot-password-link" 
                            onClick={() => { setShowForgot(true); setIdentifier(buyerId); }}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>
                        Don't have an account?
                        <br />
                        <Link to="/register" className="register-link">
                            Register here
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
